/**
 * Das Diagramm (HM-054).
 *
 * **Eine Komponente für das ganze System.** Sie zeichnet den Verlauf eines
 * Datenpunkts genauso wie später die Tagesbilanz einer Anlage; was sich
 * unterscheidet, steht im Vertrag (`types.ts`) und nicht in einer zweiten
 * Komponente.
 *
 * **Sie liegt in `@hausmeter/ui` und wird trotzdem nicht aus dem Hauptindex
 * ausgeliefert.** Der Hauptindex muss für React Native taugen, und uPlot
 * zeichnet auf ein `<canvas>` — beides zusammen ginge nicht. Der Weg hierher
 * ist deshalb `@hausmeter/ui/chart`. Alles Fachliche daneben (`align`, `stack`,
 * `range`, `frame`) ist frei von React **und** von uPlot: Wenn Mobile eines
 * Tages denselben Verlauf zeigt, zieht die Rechnung ohne Änderung mit und nur
 * die Zeichenschicht ist neu.
 *
 * Drei Dinge, die man an einer Diagramm-Komponente in React falsch macht:
 *
 * **Das Diagramm wird nicht bei jeder Datenänderung neu gebaut.** Neu gebaut
 * wird nur, wenn sich der *Aufbau* ändert — Reihen, Darstellungsart, Höhe.
 * Neue Werte gehen mit `setData` hinein; ein Neuaufbau alle 60 Sekunden
 * schlösse das Ablesefenster mitten im Lesen.
 *
 * **Die Breite kommt vom Platz, nicht aus einer Zahl.** Ein `viewBox` mit
 * fester Breite streckt Schrift und Linien mit; ein `ResizeObserver` gibt dem
 * Diagramm die Bildpunkte, die es wirklich hat.
 *
 * **Ohne Maus geht es auch.** Der Rahmen ist fokussierbar, die Pfeiltasten
 * bewegen das Fadenkreuz. Ein Canvas ist für eine Sprachausgabe eine leere
 * Fläche — deshalb `role="img"` mit Beschreibung, und deshalb steht in der
 * Anwendung neben jedem Diagramm eine Tabelle mit denselben Zahlen.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import uPlot from "uplot";
import { buildFrame } from "./frame.js";
import { ChartLegend } from "./legend.js";
import { mergeForWidth } from "./merge.js";
import { buildOptions } from "./options.js";
import { CSS_SERIES_COLORS, readChartPalette } from "./palette.js";
import type { ChartProps } from "./types.js";

/**
 * Zählt hoch, sobald das Thema wechselt (HM-107).
 *
 * Das Canvas hat seine Farben beim Aufbau aus den CSS-Variablen gelesen; ein
 * Wechsel auf `data-theme="dark"` erreicht es nicht von selbst, und das
 * Diagramm zeichnete weiter in Schiefer auf Schiefer. Das Attribut auf `<html>`
 * ist der Vertrag des Design-Systems für das Thema — beobachtet wird deshalb
 * genau dieses und nicht ein Speicher, den nur die Anwendung kennt.
 */
function useThemeVersion(): number {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => setVersion((current) => current + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  return version;
}

const DEFAULT_HEIGHT = 220;

/**
 * Unter dieser Breite steht das Diagramm auf einem Telefon im Hochformat und
 * wird um `NARROW_HEIGHT_FACTOR` höher: Breite fehlt dort, Höhe nicht, und ein
 * flaches Bild aus schmalen Balken liest sich am schlechtesten.
 */
const NARROW_WIDTH = 520;
const NARROW_HEIGHT_FACTOR = 1.3;

/**
 * So weit ragt das Diagramm auf schmaler Fläche in den rechten Innenabstand
 * seiner Karte (`.hm-chart.is-narrow` in `chart.css`). Dort stehen nur noch die
 * Zahlen der rechten Achse; auf einem Telefon ist jeder Bildpunkt Zeichenfläche
 * wert. Kleiner als der Innenabstand einer Karte (`md`: 16), damit nichts den
 * Rand berührt.
 */
const NARROW_BLEED = 12;

/** Der Abstand zwischen Zeichenfläche und Achsenbeschriftung — uPlots `axis.gap`. */
const AXIS_LABEL_GAP = 5;

export function Chart(given: ChartProps) {
  const outer = useRef<HTMLDivElement | null>(null);
  const host = useRef<HTMLDivElement | null>(null);

  /**
   * Die Breite des Rahmens — sie entscheidet, ob Stunden zusammengefasst werden
   * (`merge.ts`). Gemessen vor dem ersten Zeichnen, damit ein Telefon nicht erst
   * 24 Balken und einen Augenblick später acht zeigt.
   */
  const [width, setWidth] = useState(0);
  const [rightAxisAt, setRightAxisAt] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = outer.current;
    if (element === null) return undefined;
    setWidth(Math.floor(element.clientWidth));
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const narrow = width > 0 && width < NARROW_WIDTH;
  const props = useMemo(
    () => mergeForWidth(given, narrow ? width + NARROW_BLEED : width),
    [given, width, narrow],
  );
  const plot = useRef<uPlot | null>(null);
  const cursorAt = useRef<number | null>(null);

  /**
   * Der jeweils neueste Stand für die Zusatzstücke. Sie zeichnen außerhalb des
   * React-Ablaufs und dürfen deshalb nicht auf einen eingeschlossenen Wert von
   * vor drei Zustandsänderungen sehen.
   */
  const latest = useRef(props);
  latest.current = props;

  /* Für Legende und Werte: Farben als `var(…)`, das DOM löst sie im Thema auf. */
  const frame = buildFrame(props, CSS_SERIES_COLORS);
  const baseHeight = props.height ?? DEFAULT_HEIGHT;
  const height = narrow ? Math.round(baseHeight * NARROW_HEIGHT_FACTOR) : baseHeight;
  const empty = frame.empty;
  const themeVersion = useThemeVersion();

  /**
   * Der Aufbau in einer Zeichenkette: Reihen, Art, Farbe, Stapelung, Höhe.
   * Werte stehen bewusst **nicht** darin — sie ändern sich im Minutentakt und
   * würden das Diagramm jedes Mal neu bauen.
   */
  const structure = useMemo(
    () =>
      [
        props.series
          .map(
            (entry) =>
              `${entry.id}|${entry.mark ?? "line"}|${entry.band === true}` +
              `|${entry.quantity ?? ""}|${entry.color ?? ""}|${entry.fade ?? 0}|${entry.label}` +
              `|${entry.axis ?? "left"}|${entry.dashed === true}`,
          )
          .join("//"),
        props.stacked === true,
        height,
        /* Das Raster gehört zum Aufbau: Zusammengefasste Stunden haben andere Abschnitte. */
        props.bucket ?? "",
      ].join("#"),
    [props.series, props.stacked, height, props.bucket],
  );

  useEffect(() => {
    const element = host.current;
    if (element === null || empty) return undefined;

    /* Für das Canvas: aufgelöste Farben aus dem Thema, denn `var(…)` kennt es nicht. */
    const palette = readChartPalette(element);
    const current = buildFrame(latest.current, palette);
    const options = buildOptions(current, palette, () => latest.current);
    /* Wo die rechte Achse beginnt — dort steht ihre Einheit, linksbündig über
       den Zahlen. Die Breite der Achsen folgt den Werten, gelesen wird deshalb
       nach jedem Zeichnen. */
    const report = (u: uPlot): void => {
      const at = (u.bbox.left + u.bbox.width) / uPlot.pxRatio + AXIS_LABEL_GAP;
      setRightAxisAt((previous) => (previous !== null && Math.abs(previous - at) < 0.5 ? previous : at));
    };
    const instance = new uPlot(
      {
        ...options,
        width: element.clientWidth || 600,
        height,
        hooks: { ...options.hooks, draw: [...(options.hooks?.draw ?? []), report] },
      },
      current.data as unknown as uPlot.AlignedData,
      element,
    );
    plot.current = instance;

    const observer = new ResizeObserver((entries) => {
      const width = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (width > 0) instance.setSize({ width, height });
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      instance.destroy();
      plot.current = null;
    };
    // Der Aufbau hängt an `structure` und am Thema; die Werte gehen über `setData` hinein.
  }, [structure, empty, height, themeVersion]);

  useEffect(() => {
    plot.current?.setData(frame.data as unknown as uPlot.AlignedData);
  }, [frame.data]);

  /**
   * Das Fadenkreuz mit den Pfeiltasten. `setCursor` erwartet Bildpunkte, also
   * wird der Zeitpunkt in eine Position umgerechnet — dieselbe, die eine Maus
   * getroffen hätte.
   */
  const move = (delta: number, absolute?: "first" | "last"): void => {
    const instance = plot.current;
    const times = instance?.data[0];
    if (instance === null || times === undefined || times.length === 0) return;

    const previous = cursorAt.current;
    const next =
      absolute === "first"
        ? 0
        : absolute === "last"
          ? times.length - 1
          : Math.min(
              times.length - 1,
              Math.max(0, (previous ?? (delta > 0 ? -1 : times.length)) + delta),
            );

    cursorAt.current = next;
    instance.setCursor({ left: instance.valToPos(times[next]!, "x"), top: 0 });
  };

  const hide = (): void => {
    cursorAt.current = null;
    // Aus dem Bild heraus: uPlot versteckt das Fadenkreuz, sobald der Zeiger
    // außerhalb der Zeichenfläche liegt.
    plot.current?.setCursor({ left: -10, top: -10 });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const actions: Record<string, () => void> = {
      ArrowRight: () => move(1),
      ArrowLeft: () => move(-1),
      Home: () => move(0, "first"),
      End: () => move(0, "last"),
      Escape: hide,
    };

    const action = actions[event.key];
    if (action === undefined) return;
    event.preventDefault();
    action();
  };

  const showLegend = props.legend ?? props.series.length > 1;
  const leftUnit = props.format.unit ?? "";
  const rightUnit = props.series.some((entry) => entry.axis === "right") ? (props.rightFormat?.unit ?? "") : "";
  const substituted = !empty && frame.substituted.some((quality) => quality !== null);

  return (
    <div className={narrow ? "hm-chart is-narrow" : "hm-chart"} ref={outer}>
      {showLegend && <ChartLegend rows={frame.rows} />}

      {/* Die Einheiten stehen einmal über ihren Achsen, nicht an jeder Zahl. */}
      {!empty && (leftUnit !== "" || rightUnit !== "") && (
        <div className="hm-chart-units" aria-hidden="true">
          <span>{leftUnit}</span>
          {rightUnit !== "" && rightAxisAt !== null && (
            <span style={{ position: "absolute", left: rightAxisAt }}>{rightUnit}</span>
          )}
        </div>
      )}

      {empty ? (
        <p className="hm-chart-empty" style={{ height }}>
          {props.emptyMessage ?? "In diesem Zeitraum ist kein Wert eingegangen."}
        </p>
      ) : (
        <div
          ref={host}
          className="hm-chart-plot"
          style={{ height }}
          role="img"
          aria-label={props.label}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onBlur={hide}
        />
      )}

      {/* Die Leiste unter einem Abschnitt braucht einen Satz, sonst ist sie eine
          Verzierung (HM-115). Er steht nur, wenn im Bild wirklich ein Ersatzwert ist. */}
      {substituted && (
        <p className="hm-chart-note">
          <span className="hm-chart-key-substitute" aria-hidden="true" />
          Ersatzwert: Die Menge ist aus den Zählerständen bekannt, ihre Verteilung auf die Zeit
          geschätzt.
        </p>
      )}
    </div>
  );
}
